import React, { useState, useEffect } from "react"
import { Button, Card, Form } from "react-bootstrap"
import axios from "axios"
import { localserver } from "../../../api/common"
import { showToast, ToastMessage } from "../../Shared/ToastMessage"
import { FormField } from "./FormField"

const UpdateAttendeeModal = ({ show, onHide, attendeeDetails, ticketList, getAttendeesByEventID, activeTab }) => {
  const [attendee, setAttendee] = useState({
    id: null,
    customFields: {},
    ticket: "",
    addOns: [],
  })
  const [guestCustomFields, setGuestCustomFields] = useState({})
  const [errors, setErrors] = useState({})

  // Parse form_details to ensure it's an array
  const parseFormDetails = (formDetails) => {
    if (typeof formDetails === "string") {
      try {
        return JSON.parse(formDetails)
      } catch (error) {
        console.error("Error parsing form_details:", error)
        return []
      }
    }
    return Array.isArray(formDetails) ? formDetails : []
  }

  // Parse form_data or guest_data
  const parseData = (data) => {
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data || "[]")
        return parsed[0]?.custom_fields || []
      } catch (error) {
        console.error("Error parsing data:", error)
        return []
      }
    }
    return Array.isArray(data) ? data : []
  }

  // Parse add_ons_data or add_ons from form_data/guest_data
  const parseAddOnsData = (data) => {
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data || "[]")
        // Handle direct array of add-ons or add_ons field
        if (Array.isArray(parsed)) {
          return parsed[0]?.add_ons || parsed
        }
        return []
      } catch (error) {
        console.error("Error parsing add_ons_data:", error)
        return []
      }
    }
    return Array.isArray(data) ? data : []
  }

  // Prepopulate form when attendeeDetails changes
  useEffect(() => {
    console.log("attendeeDetails:", attendeeDetails)
    if (attendeeDetails) {
      let parsedFormData = []
      let parsedGuestData = []
      let customFields = {}
      let guestCustomFields = {}
      let ticketId = ""
      let parsedAddOns = []

      if (attendeeDetails.isGuest) {
        parsedFormData = Array.isArray(attendeeDetails.custom_fields)
          ? attendeeDetails.custom_fields
          : parseData(attendeeDetails.guest_data)
        console.log("Guest parsedFormData:", parsedFormData)
        customFields = parsedFormData.reduce((acc, field) => {
          if (field && field.id && field.value !== undefined) {
            acc[field.id] = field.value || ""
          }
          return acc
        }, {})
        parsedGuestData = parseData(attendeeDetails.form_data)
      } else {
        parsedFormData = parseData(attendeeDetails.form_data)
        parsedGuestData = parseData(attendeeDetails.guest_data)
        customFields = parsedFormData.reduce((acc, field) => {
          if (field && field.id && field.value !== undefined) {
            acc[field.id] = field.value || ""
          }
          return acc
        }, {})
        guestCustomFields = parsedGuestData.reduce((acc, field) => {
          if (field && field.id && field.value !== undefined) {
            acc[field.id] = field.value || ""
          }
          return acc
        }, {})
        ticketId = attendeeDetails.ticket_id || ""
        parsedAddOns = parseAddOnsData(attendeeDetails.add_ons_data)
      }

      setAttendee({
        id: attendeeDetails.id || null,
        customFields,
        ticket: ticketId,
        addOns: parsedAddOns.map((addOn) => ({
          id: addOn.addon_id,
          ticket_id: addOn.ticket_id,
          variant: addOn.variant_name,
          quantity: addOn.quantity,
        })),
      })
      setGuestCustomFields(guestCustomFields)
      setErrors({})
      console.log("attendee.customFields:", customFields)
      console.log("guestCustomFields:", guestCustomFields)
      console.log("attendee.addOns:", attendee.addOns)
    }
  }, [attendeeDetails])

  // Validation function for attendee and guest details
  const validateAttendee = () => {
    const newErrors = {}
    if (!attendeeDetails.isGuest) {
      const ticket = ticketList.find((t) => t.id === Number.parseInt(attendee.ticket))
      if (!ticket) {
        newErrors.ticket = "Please select a ticket"
      }

      const formDetails = parseFormDetails(ticket?.form_details)
      if (formDetails.length > 0) {
        formDetails.forEach((field) => {
          const value = attendee.customFields[field.id]
          const errorKey = field.id

          if(typeof(value)===String){
            if (field.required && (!value || value.trim() === "")) {
              newErrors[errorKey] = `${field.label} is required`
            }
          }else{
            if (field.required && (!value || value === "")) {
              newErrors[errorKey] = `${field.label} is required`
            }
          }
          if (field.type === "email" && value && value.trim() !== "") {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              newErrors[errorKey] = `Invalid email format for ${field.label}`
            }
          }
        })
      }
    } else {
      Object.entries(attendee.customFields).forEach(([key, value]) => {
        if (!value || value.trim() === "") {
          newErrors[key] = `${key.replace("_", " ").toUpperCase()} is required`
        }
        if (key.includes("email") && value && value.trim() !== "") {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            newErrors[key] = "Invalid email format"
          }
        }
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAttendeeChange = (fieldId, value) => {
    setAttendee({
      ...attendee,
      customFields: {
        ...attendee.customFields,
        [fieldId]: value,
      },
    })
    setErrors((prev) => ({ ...prev, [fieldId]: null }))
  }

  const handleSubmit = async () => {
    if (!validateAttendee()) {
      showToast("Please fix the errors in the form", "error")
      return
    }

    if (!attendee.id) {
      showToast("No attendee selected for update", "error")
      return
    }

    try {
      let formData = []
      let guestData = []
      let ticket

      if (!attendeeDetails.isGuest) {
        ticket = ticketList.find((t) => t.id === Number.parseInt(attendee.ticket))
        const formDetails = parseFormDetails(ticket?.form_details)
        formData = [
          {
            custom_fields: Object.entries(attendee.customFields || {}).map(([id, value]) => {
              const field = formDetails.find((f) => f.id === id) ||
                parseData(attendeeDetails.form_data).find((f) => f.id === id) || {
                  id,
                  type: id,
                  label: id,
                  name: id,
                }
              // const field = formDetails.find((f) => f.id === id) || parseData(attendeeDetails.form_data).find((f) => f.id === id) || {
              //   id,
              //   type: id.includes("email") ? "email" : "text",
              //   label: id.includes("firstname") ? "First Name" : id.includes("lastname") ? "Last Name" : id.includes("email") ? "Email Address" : "Phone",
              //   name: id.includes("firstname") ? "first_name" : id.includes("lastname") ? "last_name" : id.includes("email") ? "email_address" : "",
              // };
              return {
                id,
                type: field.type,
                label: field.label,
                name: field.name,
                value,
              }
            }),
            add_ons: attendee.addOns.map((addOn) => ({
              addon_id: addOn.id,
              variant_name: addOn.variant,
              quantity: addOn.quantity,
            })),
          },
        ]
        if (Object.keys(guestCustomFields).length > 0) {
          guestData = [
            {
              custom_fields: Object.entries(guestCustomFields || {}).map(([id, value]) => {
                const field = formDetails.find((f) => f.id === id) ||
                  parseData(attendeeDetails.form_data).find((f) => f.id === id) || {
                    id,
                    type: id,
                    label: id,
                    name: id,
                  }
                // const field = parseData(attendeeDetails.guest_data).find((f) => f.id === id) || {
                //   id,
                //   type: id.includes("email") ? "email" : "text",
                //   label: id.includes("firstname") ? "First Name" : id.includes("lastname") ? "Last Name" : id.includes("email") ? "Email Address" : "Phone",
                //   name: id.includes("firstname") ? "first_name" : id.includes("lastname") ? "last_name" : id.includes("email") ? "email_address" : "",
                // };
                return {
                  id,
                  type: field.type,
                  label: field.label,
                  name: field.name,
                  value,
                }
              }),
              add_ons: parseAddOnsData(attendeeDetails.guest_data),
            },
          ]
        }
      } else {
        guestData = [
          {
            custom_fields: Object.entries(attendee.customFields || {}).map(([id, value]) => {
              const field = (Array.isArray(attendeeDetails.custom_fields)
                ? attendeeDetails.custom_fields
                : parseData(attendeeDetails.guest_data)
              ).find((f) => f.id === id) || {
                id,
                type: id,
                label: id,
                name: id,
                // type: id.includes("email") ? "email" : "text",
                // label: id.includes("firstname") ? "First Name" : id.includes("lastname") ? "Last Name" : id.includes("email") ? "Email Address" : "Phone",
                // name: id.includes("firstname") ? "first_name" : id.includes("lastname") ? "last_name" : id.includes("email") ? "email_address" : "",
              }
              return {
                id,
                type: field.type,
                label: field.label,
                name: field.name,
                value,
              }
            }),
            add_ons: parseAddOnsData(attendeeDetails.guest_data),
          },
        ]
        formData = [
          {
            custom_fields: parseData(attendeeDetails.form_data).map((field) => ({
              id: field.id,
              type: field.type,
              label: field.label,
              name: field.name,
              value: field.value || "",
            })),
            add_ons: parseAddOnsData(attendeeDetails.form_data),
          },
        ]
      }

      const payload = {
        attendee_id: attendee.id,
        event_id: Number.parseInt(localStorage.getItem("eventId")) || attendeeDetails.event_id,
        form_data: formData,
        guest_data: guestData,
      }

      console.log("Payload for update:", payload)

      const response = await axios.post(`${localserver}/update_attendee_details`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      })

      if (response.data.success) {
        showToast(`${attendeeDetails.isGuest ? "Guest" : "Attendee"} updated successfully`, "success")
         getAttendeesByEventID({ status: activeTab }, true)
        onHide()
      } else {
        showToast(`Failed to update ${attendeeDetails.isGuest ? "guest" : "attendee"}`, "error")
      }
    } catch (error) {
      showToast(`Error updating ${attendeeDetails.isGuest ? "guest" : "attendee"}`, "error")
      console.error(`Error updating ${attendeeDetails.isGuest ? "guest" : "attendee"}:`, error)
    }
  }

  const sidebarModalStyle = {
    sidebar: { height: "100%", overflowY: "auto" },
    header: {
      borderBottom: "1px solid #e9ecef",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    closeButton: { background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
    body: { overflowY: "auto", flex: 1 },
    quantityControl: { display: "flex", alignItems: "center" },
    quantityButton: { padding: "5px", margin: "0 5px", cursor: "pointer" },
    quantityInput: { width: "40px", textAlign: "center", border: "1px solid #ced4da", borderRadius: "4px" },
  }

  if (!show) return null

  let formFieldDefinitions = []
  if (attendeeDetails.isGuest) {
    // For guests, try to get field definitions from the original form structure
    formFieldDefinitions = Array.isArray(attendeeDetails.custom_fields)
      ? attendeeDetails.custom_fields
      : parseData(attendeeDetails.guest_data)
  } else {
    // For regular attendees, get form definitions from ticket
    const ticket =
      ticketList.find((t) => t.id === Number.parseInt(attendee.ticket)) ||
      ticketList.find((t) => t.id === Number.parseInt(attendeeDetails.ticket_id))
    if (ticket && ticket.form_details) {
      formFieldDefinitions = parseFormDetails(ticket.form_details)
    } else {
      // Fallback to parsed form data
      formFieldDefinitions = parseData(attendeeDetails.form_data)
    }
  }

  const parsedFormData = attendeeDetails.isGuest
    ? Array.isArray(attendeeDetails.custom_fields)
      ? attendeeDetails.custom_fields
      : parseData(attendeeDetails.guest_data)
    : parseData(attendeeDetails.form_data)

  return (
    <>
      <ToastMessage />
      <div
        className={`extraspace_search_div1 add_attendites_model search_div${show ? " open" : ""}`}
        style={sidebarModalStyle.sidebar}
      >
        <div className="card member_master_sidebar_scrool">
          <div className="member_edit_pop_flex mb-3">
            <h3>{attendeeDetails.isGuest ? "Update Guest" : "Update Attendee"}</h3>
            <button className="search_toggle btn btn-danger" onClick={onHide}>
              <i className="fa fa-times" />
            </button>
          </div>

          <div style={sidebarModalStyle.body}>
            <Card>
              <Card.Body>
                <Form>
                  {/* {!attendeeDetails.isGuest && (
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Ticket <span className="text-danger">*</span>
                      </Form.Label>
                      <select
                        className={`form-control ${errors.ticket ? "is-invalid" : ""}`}
                        value={attendee.ticket}
                        onChange={(e) => {
                          setAttendee({ ...attendee, ticket: e.target.value, customFields: {}, addOns: [] });
                          setErrors((prev) => ({ ...prev, ticket: null }));
                        }}
                      >
                        <option value="">Select a ticket</option>
                        {ticketList?.map((ticket) => (
                          <option key={ticket.id} value={ticket.id}>
                            {ticket.ticket_name || `Ticket ${ticket.id}`}
                          </option>
                        ))}
                      </select>
                      <Form.Control.Feedback type="invalid">{errors.ticket}</Form.Control.Feedback>
                    </Form.Group>
                  )} */}

                  {formFieldDefinitions.length > 0 ? (
                    formFieldDefinitions.map((field) => (
                      <FormField
                        key={field.id}
                        field={{
                          id: field.id,
                          name: field.name,
                          label: field.label,
                          type: field.type,
                          required: field.required || true,
                          options: field.options || undefined, // Include options for select fields
                        }}
                        value={attendee.customFields[field.id] || ""}
                        onChange={(value) => handleAttendeeChange(field.id, value)}
                        error={errors[field.id]}
                      />
                    ))
                  ) : (
                    <p className="text-center text-muted py-4">
                      {attendeeDetails.isGuest ? "No guest fields available." : "No attendee fields available."}
                    </p>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </div>

          <div className="footer_card">
            <button className="btn btn-info" onClick={onHide}>
              Back
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Update
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default UpdateAttendeeModal
